import { db } from './firebase';
// import { getOrCreateAnonymousId } from '../utils/anonymousUtils';
import { getUserCredits, deductCredits } from './firebaseUtils';
import { getCreditCost } from '../config/creditCosts';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp, 
  increment,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  arrayUnion,
  addDoc
} from "firebase/firestore";
import { auth } from './firebase';
import { emitCreditsUpdated } from '../hooks/useCredits';

/**
 * Initialize or get usage document for a user
 * @param {string} uid - User ID
 */
const initializeUsage = async (userId) => {
  const usageRef = doc(db, "usage", userId);
  const usageDoc = await getDoc(usageRef);

  if (!usageDoc.exists()) {
    const today = new Date().toISOString().split('T')[0];
    
    await setDoc(usageRef, {
      daily: {
        [today]: {
          StoryBook: 0,
          QuickStories: 0,
          PhotoBook: 0,
          lastUpdated: serverTimestamp()
        }
      },
      isAnonymous: userId.startsWith('anon_'),
      createdAt: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });
  }
};

/**
 * Track usage of a feature for a user
 * @param {string} uid - User ID
 * @param {string} feature - Feature being used
 */
export const trackFeatureUsage = async (uid, feature) => {
  try {
    const userId = uid;
    const dateKey = new Date().toISOString().split('T')[0];

    const usageRef = doc(db, "usage", userId);
    await setDoc(usageRef, {
      daily: {
        [dateKey]: {
          [feature]: increment(1),
          lastUpdated: serverTimestamp()
        }
      },
      lastUpdated: serverTimestamp()
    }, { merge: true });

    return true;
  } catch (error) {
    // console.error('Error tracking feature usage:', error);
    throw error;
  }
};

/**
 * Get current usage statistics for a user
 * @param {string} uid - User ID
 * @returns {Promise<Object>} Usage statistics
 */
export const getUserUsageStats = async (uid) => {
  try {
    await initializeUsage(uid);
    const usageDoc = await getDoc(doc(db, "usage", uid));
    return usageDoc.data();
  } catch (error) {
    // console.error('Error getting usage stats:', error);
    throw error;
  }
};

// Cleanup function for old anonymous data
export const cleanupAnonymousUsage = async () => {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const usageRef = collection(db, "usage");
    const q = query(
      usageRef,
      where("isAnonymous", "==", true),
      where("lastUpdated", "<", thirtyDaysAgo)
    );
    
    const snapshot = await getDocs(q);
    let deletedCount = 0;
    
    for (const doc of snapshot.docs) {
      await deleteDoc(doc.ref);
      deletedCount++;
    }
    
    // console.log(`Cleaned up ${deletedCount} old anonymous usage records`);
  } catch (error) {
    // console.error('Error cleaning up anonymous usage:', error);
    throw error;
  }
};

/**
 * Gets today's date in YYYY-MM-DD format
 * @returns {string} formatted date
 */
const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Logs a story generation event in daily usage stats
 * @returns {Promise} - Promise that resolves when the usage is logged
 */
export const logStoryGeneration = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in - usage not tracked');
      return;
    }

    const today = getTodayDate();
    const usageRef = doc(db, 'usage', user.uid);
    
    // Get the current usage document
    const usageDoc = await getDoc(usageRef);
    
    if (!usageDoc.exists()) {
      // Create new usage document if it doesn't exist
      await setDoc(usageRef, {
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        daily: {
          [today]: {
            StoryBook: 0,
            QuickStories: 1,
            PhotoBook: 0
          }
        }
      });
    } else {
      const currentData = usageDoc.data();
      const dailyData = currentData.daily || {};
      const todayData = dailyData[today] || {
        StoryBook: 0,
        QuickStories: 0,
        PhotoBook: 0
      };

      // Update the QuickStories count for today
      await updateDoc(usageRef, {
        lastUpdated: serverTimestamp(),
        [`daily.${today}`]: {
          ...todayData,
          QuickStories: (todayData.QuickStories || 0) + 1
        }
      });
    }

  } catch (error) {
    // console.error('Error logging story generation:', error);
    throw error;
  }
};

/**
 * Logs a storybook creation in daily usage stats
 * @returns {Promise} - Promise that resolves when the usage is logged
 */
export const logStorybookCreation = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in - usage not tracked');
      return;
    }

    const today = getTodayDate();
    const usageRef = doc(db, 'usage', user.uid);
    
    const usageDoc = await getDoc(usageRef);
    
    if (!usageDoc.exists()) {
      await setDoc(usageRef, {
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        daily: {
          [today]: {
            StoryBook: 1,
            QuickStories: 0,
            PhotoBook: 0
          }
        }
      });
    } else {
      const currentData = usageDoc.data();
      const dailyData = currentData.daily || {};
      const todayData = dailyData[today] || {
        StoryBook: 0,
        QuickStories: 0,
        PhotoBook: 0
      };

      await updateDoc(usageRef, {
        lastUpdated: serverTimestamp(),
        [`daily.${today}`]: {
          ...todayData,
          StoryBook: (todayData.StoryBook || 0) + 1
        }
      });
    }
  } catch (error) {
    // console.error('Error logging storybook creation:', error);
    throw error;
  }
};

/**
 * Logs a photobook creation in daily usage stats
 * @returns {Promise} - Promise that resolves when the usage is logged
 */
export const logPhotoBookCreation = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in - usage not tracked');
      return;
    }

    const today = getTodayDate();
    const usageRef = doc(db, 'usage', user.uid);
    
    const usageDoc = await getDoc(usageRef);
    
    if (!usageDoc.exists()) {
      await setDoc(usageRef, {
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        daily: {
          [today]: {
            StoryBook: 0,
            QuickStories: 0,
            PhotoBook: 1
          }
        }
      });
    } else {
      const currentData = usageDoc.data();
      const dailyData = currentData.daily || {};
      const todayData = dailyData[today] || {
        StoryBook: 0,
        QuickStories: 0,
        PhotoBook: 0
      };

      await updateDoc(usageRef, {
        lastUpdated: serverTimestamp(),
        [`daily.${today}`]: {
          ...todayData,
          PhotoBook: (todayData.PhotoBook || 0) + 1
        }
      });
    }
  } catch (error) {
    // console.error('Error logging photobook creation:', error);
    throw error;
  }
};

/**
 * Logs a story narration event in daily usage stats
 * @returns {Promise} - Promise that resolves when the usage is logged
 */
export const logStoryNarration = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in - usage not tracked');
      return;
    }

    const today = getTodayDate();
    const usageRef = doc(db, 'usage', user.uid);
    
    const usageDoc = await getDoc(usageRef);
    
    if (!usageDoc.exists()) {
      // Create new usage document if it doesn't exist
      await setDoc(usageRef, {
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        daily: {
          [today]: {
            StoryBook: 0,
            QuickStories: 0,
            PhotoBook: 0,
            StoryNarration: 1  // Initialize with first narration
          }
        }
      });
    } else {
      const currentData = usageDoc.data();
      const dailyData = currentData.daily || {};
      const todayData = dailyData[today] || {
        StoryBook: 0,
        QuickStories: 0,
        PhotoBook: 0,
        StoryNarration: 0
      };

      // Update the StoryNarration count for today
      await updateDoc(usageRef, {
        lastUpdated: serverTimestamp(),
        [`daily.${today}`]: {
          ...todayData,
          StoryNarration: (todayData.StoryNarration || 0) + 1
        }
      });
    }

  } catch (error) {
    // console.error('Error logging story narration:', error);
    throw error;
  }
};

/**
 * Saves the generated story to the quickstories collection
 * @param {string} storyContent - The generated story content
 * @param {object} metadata - Story metadata (theme, age, type, etc.)
 * @returns {Promise<string>} - Promise that resolves with the story ID
 */
export const saveGeneratedStory = async (storyContent, metadata) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in - story not saved');
      return;
    }

    // Reference to the quickstories collection
    const quickstoriesRef = collection(db, 'quickstories');
    const newStoryRef = doc(quickstoriesRef); // Auto-generated ID

    const storyData = {
      // User information
      userId: user.uid,
      userEmail: user.email || null,
      userName: user.displayName || null,

      // Story metadata
      storyTheme: metadata.storyTheme,
      ageGroup: metadata.ageGroup,
      storyType: metadata.storyType,
      language: metadata.language,
      characterType: metadata.characterType,

      // Story content
      content: storyContent,

      // Timestamps
      createdAt: serverTimestamp(),
      
      // Additional metadata
      isPublic: false, // Default to private
      likes: 0,
      views: 0,
      
      // Story status
      status: 'active'
    };

    await setDoc(newStoryRef, storyData);
    return newStoryRef.id; // Return the generated story ID
  } catch (error) {
    // console.error('Error saving generated story:', error);
    throw error;
  }
};

/**
 * Saves the generated storybook to the storybook collection
 * @param {string} storyContent - The generated story content
 * @param {object} metadata - Story metadata
 * @param {array} images - Generated images (optional)
 * @param {array} urls - S3 URLs for images (optional)
 * @returns {Promise<string>} - Promise that resolves with the storybook ID
 */
export const saveStorybook = async (storyContent, metadata, images = null, urls = null) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in - storybook not saved');
      return;
    }

    // First save the base data
    const baseData = {
      // User information
      userId: user.uid,
      userEmail: user.email || '',
      userName: user.displayName || '',

      // Story metadata
      storyTheme: String(metadata.storyTheme || ''),
      ageGroup: String(metadata.ageGroup || ''),
      storyType: String(metadata.storyType || ''),
      language: String(metadata.language || 'English'),
      characterType: String(metadata.characterType || ''),

      // Story content
      content: String(storyContent || ''),

      // Image generation settings
      imageSettings: {
        artStyle: String(metadata.artStyle || ''),
        colorTheme: String(metadata.colorTheme || ''),
        illustrationStyle: String(metadata.illustrationStyle || '')
      },

      // Timestamps
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),
      
      // Additional metadata
      isPublic: false,
      likes: 0,
      views: 0,
      status: 'active'
    };

    // Create a new document reference
    const storybookRef = collection(db, 'storybook');
    const newStorybookRef = doc(storybookRef);

    // First save the base data
    await setDoc(newStorybookRef, baseData);

    // If we have URLs, update the document with them
    if (Array.isArray(urls) && urls.length > 0) {
      // Create a clean array of URLs
      const cleanUrls = urls.map(url => String(url));
      
      // Update the document with just the URLs
      await updateDoc(newStorybookRef, {
        imageUrls: cleanUrls
      });
    }

    return newStorybookRef.id;

  } catch (error) {
    // console.error('Error saving storybook:', error);
    throw error;
  }
};

/**
 * Saves the generated photobook to the photobook collection
 * @param {string} storyContent - The generated story content
 * @param {object} metadata - Story metadata
 * @param {array} uploadedPhotos - Array of uploaded photos
 * @param {array} generatedImages - Array of AI-generated images (optional)
 * @param {array} imageUrls - Array of S3 URLs for images (optional)
 * @returns {Promise<string>} - Promise that resolves with the photobook ID
 */
export const savePhotoBook = async (storyContent, metadata, uploadedPhotos, generatedImages = null, imageUrls = null) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in - photobook not saved');
      return;
    }

    // First try saving without the arrays
    const baseData = {
      // User information
      userId: user.uid,
      userEmail: user.email || '',
      userName: user.displayName || '',

      // Child information
      childName: String(metadata.childName || ''),
      childAge: String(metadata.childAge || ''),
      childGender: String(metadata.childGender || ''),
      birthMonth: String(metadata.birthMonth || ''),
      hobbies: String(metadata.hobbies || ''),

      // Story details
      storyTheme: String(metadata.storyTheme || ''),
      storyType: String(metadata.storyType || ''),
      language: String(metadata.language || 'English'),
      content: String(storyContent || ''),

      // Image settings
      imageSettings: {
        artStyle: String(metadata.artStyle || ''),
        colorTheme: String(metadata.colorTheme || ''),
        illustrationStyle: String(metadata.illustrationStyle || '')
      },

      // Timestamps
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),

      // Status and visibility
      isPublic: false,
      status: 'active',

      // Analytics
      views: 0,
      likes: 0,
      shares: 0
    };

    // Create a new document reference
    const photobookRef = collection(db, 'photobook');
    const newPhotobookRef = doc(photobookRef);

    // First save the base data
    await setDoc(newPhotobookRef, baseData);

    // If we have URLs, update the document with them
    if (Array.isArray(imageUrls) && imageUrls.length > 0) {
      // Create a clean array of URLs
      const cleanUrls = imageUrls.map(url => String(url));
      
      // Update the document with just the URLs
      await updateDoc(newPhotobookRef, {
        imageUrls: cleanUrls
      });
    }

    return newPhotobookRef.id;

  } catch (error) {
    // console.error('Error saving photobook:', error);
    throw error;
  }
};

/**
 * Saves the generated narration to the narrations collection
 * @param {string} storyText - The story text that was narrated
 * @param {string} narrator - The narrator voice used
 * @param {string} audioUrl - The S3 URL of the narration
 * @returns {Promise<string>} - Promise that resolves with the narration ID
 */
export const saveNarration = async (storyText, narrator) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No user logged in - narration not saved');
      return;
    }

    // Reference to the narrations collection
    const narrationsRef = collection(db, 'narrations');
    const newNarrationRef = doc(narrationsRef); // Auto-generated ID

    const narrationData = {
      // User information
      userId: user.uid,
      userEmail: user.email || null,
      userName: user.displayName || null,

      // Narration metadata
      storyText: storyText,
      narrator: narrator,
      
      // Timestamps
      createdAt: serverTimestamp(),
      
      // Status
      status: 'completed',
      
      // Analytics
      duration: null, // Can be added later if needed
      characterCount: storyText.length,
      
      // S3 path info (for future reference)
      s3Path: `narrations/${user.uid}/${newNarrationRef.id}.mp3`
    };

    await setDoc(newNarrationRef, narrationData);
    return newNarrationRef.id;
  } catch (error) {
    // console.error('Error saving narration:', error);
    throw error;
  }
};

/**
 * Check if a user has enough credits to use a feature
 * @param {string} uid - User ID
 * @param {string} feature - Feature name to check
 * @returns {Promise<{allowed: boolean, credits: number, cost: number}>}
 */
export const checkCreditsForFeature = async (uid, feature) => {
  try {
    if (!uid) {
      return {
        allowed: false,
        credits: 0,
        cost: getCreditCost(feature)
      };
    }

    const credits = await getUserCredits(uid);
    const cost = getCreditCost(feature);
    
    return {
      allowed: credits >= cost,
      credits,
      cost
    };
  } catch (error) {
    // console.error('Error in checkCreditsForFeature:', error);
    throw error;
  }
};

/**
 * Use credits for a feature and track the usage
 * @param {string} uid - User ID
 * @param {string} feature - Feature being used
 * @returns {Promise<boolean>} Whether the operation was successful
 */
export const spendCreditsForFeature = async (uid, feature) => {
  try {
    if (!uid) {
      return false;
    }

    const cost = getCreditCost(feature);
    if (cost <= 0) {
      return true; // No cost, allow usage
    }

    // Deduct credits and track the transaction
    const success = await deductCredits(uid, cost, feature);
    
    if (success) {
      // Also track the feature usage for analytics
      await trackFeatureUsage(uid, feature);
      
      // Emit the credits updated event to update UI components
      emitCreditsUpdated();
      
      return true;
    }
    
    return false;
  } catch (error) {
    // console.error('Error using credits for feature:', error);
    return false;
  }
};

// Keep the old name for backward compatibility
export const useCreditsForFeature = spendCreditsForFeature; 